import { Component, OnInit, OnDestroy, TemplateRef } from '@angular/core';
import {
    DEFAULT_CONTROL_DEBOUNCE_TIME_MS,
    DEFAULT_ITEMS_PAGINATION_CONFIG,
    DEFAULT_PAGE_TITLE,
    DEFAULT_PAGINATION_CONFIG, DEFAULT_PAGINATION_CONFIG_LAZY, DEFAULT_SCROLL_THROTTLE_TIME_MS,
    VIRTUAL_SCROLL_HEIGHT_SETTINGS
} from "../../shared.constants";
import { IPaginationConfig } from "../../types";
import { LazyLoadingPagination, Search, UtilService, Column, ChipDropdown, MenuItems } from '@material-library';
import { BehaviorSubject, Subject } from 'rxjs';
import { FileItem } from "../../models";
import { FileItemService } from "../../../file/file-item.service";
import { ActivatedRoute } from "@angular/router";

type TPaginationConfig = IPaginationConfig & { 
    isScrollUpdate?: boolean
    isInitialLoading?: boolean
} 

@Component({
    selector: 'app-base-file-list',
    template: '<div></div>',
})
export abstract class BaseFileListComponent implements OnInit, OnDestroy {
    protected lazyLoadingPaginationConfig: LazyLoadingPagination | null = null;
    protected paginationConfig: TPaginationConfig = DEFAULT_ITEMS_PAGINATION_CONFIG;
    protected paginationConfig$ = new BehaviorSubject<TPaginationConfig>(null);
    protected isPaginationInitialized = false;
    protected itemPerPage = DEFAULT_PAGINATION_CONFIG.itemsPerPage;
    protected currentIndex = 0;
    public searchConfig: Search;
    public showImageKey: string = 'thumbsrc';
    public tileSizeCondition = {
        tileSizeKey: 'type',
        tileSizeValue: 'file'
    };
    public filterIcons;
    public isNameDesc: boolean;
    public isDateDesc: boolean;
    public isSizeDesc: boolean;
    public fileSorterConfig: ChipDropdown;
    public PAGE_TITLE = DEFAULT_PAGE_TITLE;
    public isLoading: boolean = false;
    //TODO should be enum here or boolean
    public showDeleted: number = 0;
    //all items list
    public fileList: FileItem[] = [];
    //displayed items list
    public dataSource: FileItem[] = [];
    public fileListColumns: Column[] = [];
    abstract cellTemplateRef: TemplateRef<any>;
    abstract iconTemplateRef: TemplateRef<any>;
    abstract listService: any;
    protected destroySubscriptions$ = new Subject<void>();

    public minusHeightForListViewDesktop = VIRTUAL_SCROLL_HEIGHT_SETTINGS.desktop.listView;
    public minusHeightForListViewMobile = VIRTUAL_SCROLL_HEIGHT_SETTINGS.mobile.listView;
    public minusHeightForGridViewDesktop = VIRTUAL_SCROLL_HEIGHT_SETTINGS.desktop.gridView;
    public minusHeightForGridViewMobile = VIRTUAL_SCROLL_HEIGHT_SETTINGS.mobile.gridView;
    public trackByFn = ((i, item: FileItem) => {
      return `${item.sync_id}-${item.is_favorite ? 1 : 0}`;
    });
    
    protected constructor(protected utils: UtilService, protected fileItemService: FileItemService, public route: ActivatedRoute) {
        history.replaceState({}, '');
    }

    public ngOnInit(): void {
        this.searchConfig = {
            placeholder: '',
            label: 'Filter in this folder',
            size: 'default',
            showTrailAdornment: false,
            inputChange: this.utils.debounce(this.onSearch, DEFAULT_CONTROL_DEBOUNCE_TIME_MS),
            onSearch: this.onSearch,
            searchValue: '',
            isRounded: true,
            isCancellable: true,
            leadAdornment: {
                type: 'icon',
                size: 'large',
                iconName: 'filter-tube',
                enabled: true
            }
        } as Search;
        this.showDeleted = this.route.snapshot.queryParams['showDeleted'] || 0;
        this.updateFilterIcons();
       
        //sorter config for mobile view
        this.fileSorterConfig = {
            size: 'x-large',
            label: 'Sort by:',
            showLabel: true,
            list: [
                { name: 'Name (A-Z)', value: 'nameAsc' },
                { name: 'Name (Z-A)', value: 'nameDesc' },
                { name: 'Date modified (oldest to newest)', value: 'dateOldest' },
                { name: 'Date modified (newest to oldest)', value: 'dateNewest' },
                { name: 'Size (smallest to largest)', value: 'sizeAsc' },
                { name: 'Size (largest to smallest)', value: 'sizeDesc' }
            ],
            value: this.getCurrentSortOption(),
            onSelectionChange: this.onSortChange
        }
    }

    public ngAfterViewInit() {
        this.assignTemplateRefs();
        this.updateDataContainerScroll();
    }

    public ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
        history.replaceState({}, '')
    }

    protected initializePagination(): void {
        this.lazyLoadingPaginationConfig = {
            ...DEFAULT_PAGINATION_CONFIG_LAZY,
            onScrolled: this.handleScroll,
        } as LazyLoadingPagination;
        this.resetPaginationToDefault();
    }
    
    private resetPaginationToDefault() {
        const total = this.fileList.length;
        const itemsPerPage = DEFAULT_PAGINATION_CONFIG.itemsPerPage;
        const currentPage = 1;
        this.paginationConfig = {
            total,
            currentPage,
            itemsPerPage,
            numPages: Math.ceil(total / itemsPerPage),
            viewOffset: 0,
            viewEnd: itemsPerPage
        };
        this.paginationConfig$.next({
            ...this.paginationConfig,
            isInitialLoading: !this.isPaginationInitialized,
            isScrollUpdate: false
        });
        this.isPaginationInitialized = true;
    }

    protected setPageData() {
        if (this.currentIndex === 0) {
            let nextIndex = this.currentIndex + this.itemPerPage;
            this.currentIndex = nextIndex;
        }
        this.dataSource = this.fileList.slice(0, this.currentIndex);
    }

    protected updateLazyLoadingData = () => {
        let nextIndex = this.currentIndex + this.itemPerPage;
        nextIndex = nextIndex >= this.fileList.length ? this.fileList.length : nextIndex;

        this.dataSource = this.fileList.slice(0, nextIndex);
        this.currentIndex = nextIndex;

        this.handlePageChange({
            pageIndex: Math.ceil((this.currentIndex / this.itemPerPage) - 1)
        })
    }

    protected assignTemplateRefs() {
        this.fileListColumns = this.fileListColumns.map(column => {
            if (column.key === 'name') {
                return { ...column, templateRef: this.cellTemplateRef };
            }
            if (column.key === 'head') {
                return { ...column, templateRef: this.iconTemplateRef };
            }
            return column;
        }) as Column[];
    }

    public getFileIcon(item: FileItem | Record<string, any>): string {
        return this.fileItemService.getFileTypeIcon(item);
    }

    public handleScroll = async (): Promise<void> => {
        if (!this.loadMore) return Promise.resolve();

        return new Promise<void>((resolve) => {
            setTimeout(() => {
                this.updateLazyLoadingData();
                resolve()
            }, DEFAULT_SCROLL_THROTTLE_TIME_MS);
        })

    }

    public updateFilterIcons(): void {
        this.filterIcons = [
            {
                iconName: !Number(this.showDeleted) ? 'delete-view' : 'delete-view-hide',
                name: !!Number(this.showDeleted) ? 'Hide deleted files' : 'Show deleted files',
                size: 'large',
                onClick: () => { 
                    this.onFilterIconClicked();
                    this.updateFilterIcons();
                } ,
                isSelected: Number(this.showDeleted) === 1
            }
        ]
    }
    protected handleFilterOrSortingChanged(): void {
        this.isPaginationInitialized = false;
        this.currentIndex = 0;
        this.resetPaginationToDefault();
    }

    protected handlePageChange(event: { pageIndex: number }): void {
        const { pageIndex } = event;
        const { itemsPerPage } = this.paginationConfig
        const total = this.fileList.length;
        if (pageIndex === 0) return;

        this.paginationConfig = {
            total: total,
            currentPage: pageIndex + 1,
            itemsPerPage,
            numPages: Math.ceil(total / itemsPerPage),
            viewOffset: pageIndex * itemsPerPage,
            viewEnd: Math.min(pageIndex * itemsPerPage + itemsPerPage, total)
        };
        this.paginationConfig$.next({ ...this.paginationConfig, isInitialLoading: false, isScrollUpdate: true });
    }

    protected getCurrentSortOption() {
        return this.isNameDesc !== null ? (this.isNameDesc ? 'nameDesc' : 'nameAsc') :
            this.isDateDesc !== null ? (this.isDateDesc ? 'dateNewest' : 'dateOldest') :
                this.isSizeDesc !== null ? (this.isSizeDesc ? 'sizeDesc' : 'sizeAsc') :
                    'nameAsc';  // default return value if none of the conditions match
    }
    
    //this sorting used on mobile view files page
    protected onSortChange = (menuItem: MenuItems | string) => {
        const value = !menuItem ? 'nameAsc' : (typeof menuItem === 'string') ? menuItem : menuItem.value;
        switch (value) {
            case 'nameAsc':
                this.listService.sortList('name', true);
                break;
            case 'nameDesc':
                this.listService.sortList('name', false);
                break;
            case 'dateOldest':
                this.listService.sortList('usertime', false);
                break;
            case 'dateNewest':
                this.listService.sortList('usertime', true);
                break;
            case 'sizeAsc':
                this.listService.sortList('size', false);
                break;
            case 'sizeDesc':
                this.listService.sortList('size', true);
                break;
            default:
                break;
        }
    }


    protected get countText() {
        if (this.isLoading) {
            return ' ';
        } else {
            return `${this.fileList.length} ${this.showDeleted ? ' deleted ' : ''} ${this.fileList.length === 1 ? 'item' : 'items'}`;
        }
    }

    protected abstract initColumns(): void;

    protected abstract updateDataContainerScroll(): void;

    protected abstract onSearch: (searchString: string) => void;

    protected abstract onFilterIconClicked(): void;
    public get loadMore(): boolean {
        return this.fileList.length > this.dataSource.length
    };

}
